import React, { useState } from "react";
import { Autocomplete, TextField, Chip, Stack } from "@mui/material";

const ReusableMultiCreatableComponent = ({
  label,
  placeholder,
  options = [],
  selectedOptions = [],
  setSelectedOptions,
  disabledValue,
  optionLabel = "name",
  error,
  helperText,
  requireValue,
}) => {
  const [inputValue, setInputValue] = useState("");

  const normalized = (value = "") => value.trim().toLowerCase();

  const handleAddValue = (rawValue) => {
    const value = rawValue?.trim();
    if (!value) return;

    // Prevent duplicate (case-insensitive)
    if (
      selectedOptions.some(
        (item) => normalized(item[optionLabel]) === normalized(value)
      )
    ) {
      setInputValue("");
      return;
    }

    // If it already exists in options, use that object
    const existingOption = options.find(
      (opt) => normalized(opt[optionLabel]) === normalized(value)
    );

    const newItem =
      existingOption ||
      {
        id: null,                 // no id for custom item
        [optionLabel]: value,
        isCustom: true,
      };

    setSelectedOptions([...selectedOptions, newItem]);
    setInputValue("");
  };

  const handleDelete = (itemToRemove) => {
    setSelectedOptions(
      selectedOptions.filter(
        (item) => item[optionLabel] !== itemToRemove[optionLabel]
      )
    );
  };

  return (
    <Stack spacing={1}>
      <Autocomplete
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: 0,
          },
        }}
        disabled={disabledValue}
        freeSolo
        options={options.filter(
          (option) =>
            !selectedOptions.some(
              (selected) =>
                normalized(selected[optionLabel]) ===
                normalized(option[optionLabel])
            )
        )}
        getOptionLabel={(option) => {
          // option can be string (freeSolo) or object
          if (typeof option === "string") return option;
          return option[optionLabel] || "";
        }}
        inputValue={inputValue}
        onInputChange={(event, newValue) => {
          setInputValue(newValue);
        }}
        onChange={(event, newValue) => {
          // When selecting from dropdown
          if (!newValue) return;

          if (typeof newValue === "string") {
            // freeSolo input confirmed via selection
            handleAddValue(newValue);
          } else if (newValue && newValue[optionLabel]) {
            handleAddValue(newValue[optionLabel]);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={requireValue}
            InputLabelProps={{
              sx: {
                lineHeight: 1,
                top: -4,
              },
            }}
            onKeyDown={(e) => {
              // Press Enter / Tab / comma to create a chip from text
              if (
                (e.key === "Enter" || e.key === "Tab" || e.key === ",") &&
                inputValue.trim()
              ) {
                e.preventDefault();
                handleAddValue(inputValue);
              }
            }}
          />
        )}
        className="mb-2"
      />

      {/* Chips Display */}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {selectedOptions?.map((option, index) => (
          <Chip
            key={option.id ?? `${option[optionLabel]}-${index}`}
            label={option[optionLabel]}
            onDelete={() => handleDelete(option)}
            className="gn-actionbtn"
            disabled={disabledValue}
            sx={{ marginBottom: "17px" }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default ReusableMultiCreatableComponent;
